import { LicenseStatus } from '@trip-ux/constants';
import { dayjs } from '@trip-ux/common';
import { ValidationErrors } from 'final-form';
import { LicenseType } from '@trip-ux/constants';

export const formatLicenseDate = (
  dateString: string | Date,
  calendarLabels?: { dateFormat?: string } | null
): string => {
  if (!dateString) {
    return '';
  }

  const dateFormat = calendarLabels?.dateFormat;
  const fallbackFormat = 'YYYY-MM-DD';
  if (dateString instanceof Date) {
    const parsedDate = dayjs(dateString);
    if (!parsedDate.isValid()) {
      return '';
    }
    return parsedDate.format(dateFormat || fallbackFormat);
  }
  if (dateFormat) {
    const alreadyFormatted = dayjs(dateString, dateFormat, true);
    if (alreadyFormatted.isValid()) {
      return alreadyFormatted.format(dateFormat);
    }
  }
  const parsedValue = dayjs(dateString);
  if (!parsedValue.isValid()) {
    return '';
  }
  return parsedValue.format(dateFormat || fallbackFormat);
};

const validateExpiredDate = (expiredDate, effectiveDate, errors, formErrors) => {
  if (expiredDate.isBefore(effectiveDate)) {
    errors.expiredDate = formErrors.expiredDateFutureDtValid;
  }
  if (!expiredDate.isValid()) {
    errors.expiredDate = formErrors.expiredDateInvalid;
  }
};

const validateEffectiveDate = (effectiveDate, expiredDate, errors, formErrors, MIN_DATE) => {
  if (effectiveDate.isAfter(expiredDate)) {
    errors.expiredDate = formErrors.effectiveDateFutureDtValid;
  }
  if (effectiveDate.isBefore(MIN_DATE) || !effectiveDate.isValid()) {
    errors.effectiveDate = formErrors.effectiveDateInvalid;
  }
};

const validatescreeningEffectiveDate = (screeningEffectiveDate, errors, formErrors, MIN_DATE) => {
  if (!screeningEffectiveDate.isValid() || screeningEffectiveDate.isBefore(MIN_DATE)) {
    errors.screeningEffectiveDate = formErrors.screeningEffectiveDateInvalid;
  }
};

export default function (values, licenseTypeQueryParam, suggestedAgents, organizationDropDownList, calendarLabels, formErrors, MIN_DATE): ValidationErrors {
  const errors: ValidationErrors = {
    ...requiredFields(values, licenseTypeQueryParam, suggestedAgents, organizationDropDownList, formErrors)
  };

  const uiDateFormat = calendarLabels?.dateFormat;
  const effectiveDate = uiDateFormat ? dayjs(values.effectiveDate, uiDateFormat, true) : dayjs(values.effectiveDate);
  const expiredDate = uiDateFormat ? dayjs(values.expiredDate, uiDateFormat, true) : dayjs(values.expiredDate);
  const screeningEffectiveDate = uiDateFormat
    ? dayjs(values.screeningEffectiveDate, uiDateFormat, true)
    : dayjs(values.screeningEffectiveDate);

  if (!licenseTypeQueryParam && values.profileRef && !values.organizationCode) {
    errors.profileRef = formErrors.agentNameValidation;
  }

  if (values.effectiveDate) {
    validateEffectiveDate(effectiveDate, expiredDate, errors, formErrors, MIN_DATE);
  }
  if (values.expiredDate) {
    validateExpiredDate(expiredDate, effectiveDate, errors, formErrors);
  }

  if (values.screeningEffectiveDate) {
    validatescreeningEffectiveDate(screeningEffectiveDate, errors, formErrors, MIN_DATE);
  }
  return errors;
}

const requiredFields = (values, licenseTypeQueryParam, suggestedAgents, organizationDropDownList, formErrors): ValidationErrors => {
  const errors: ValidationErrors = {};
  const isStatusAuthorizedOrRenewal = [LicenseStatus.AUTHORIZED, LicenseStatus.RENEWAL_PENDING].includes(values?.status);

  const isFieldRequired = (field, condition, errorMsg) => {
    if (condition) {
      errors[field] = errorMsg;
    }
  };

  const checkOrganizationCodeRequired = () => {
    isFieldRequired('organizationCode', !licenseTypeQueryParam && !values.organizationCode, formErrors.agencyRequired);
  };

  const checkProfileRefRequired = () => {
    const shouldCheckProfileRef = !licenseTypeQueryParam && !values.profileRef && values?.licenseType?.toUpperCase() === LicenseType.AGENT;
    isFieldRequired('profileRef', shouldCheckProfileRef, formErrors.agentNameRequired);
  };

  const checkLicensedProvinceRequired = () => {
    isFieldRequired('licensedProvince', !values.licensedProvince, formErrors.provinceRequired);
  };

  const checkStatusRequired = () => {
    isFieldRequired('status', !values.status, formErrors.statusRequired);
  };

  const checkLegalNameRequired = () => {
    isFieldRequired('legalName', !values.legalName && isStatusAuthorizedOrRenewal, formErrors.legalNameRequired);
  };

  const checkLicenseNumberRequired = () => {
    isFieldRequired('licenseNumber', !values.licenseNumber && isStatusAuthorizedOrRenewal, formErrors.licenseNumberRequired);
  };

  const checkEffectiveDateRequired = () => {
    const shouldCheckEffectiveDate = !values.effectiveDate && (isStatusAuthorizedOrRenewal || values?.expiredDate);
    isFieldRequired('effectiveDate', shouldCheckEffectiveDate, formErrors.effectiveDateRequired);
  };

  const checkExpiredDateRequired = () => {
    const shouldCheckExpiredDate = !values.expiredDate && (isStatusAuthorizedOrRenewal || values?.effectiveDate);
    isFieldRequired('expiredDate', shouldCheckExpiredDate, formErrors.expiryDateRequired);
  };

  const validateAgentNameError = () => {
    const agentError = !licenseTypeQueryParam && validateAgentName(values.profileRef, suggestedAgents, formErrors);
    if (agentError) {
      errors.profileRef = agentError;
    }
  };

  const validateOrganizationCodeError = () => {
    const organizationError = !licenseTypeQueryParam && validateOrginasationCode(values.organizationCode, organizationDropDownList, formErrors);
    if (organizationError) {
      errors.organizationCode = organizationError;
    }
  };

  // Run all checks
  checkOrganizationCodeRequired();
  checkProfileRefRequired();
  checkLicensedProvinceRequired();
  checkStatusRequired();
  checkLegalNameRequired();
  checkLicenseNumberRequired();
  checkEffectiveDateRequired();
  checkExpiredDateRequired();
  validateAgentNameError();
  validateOrganizationCodeError();

  return errors;
};

const validateAgentName = (value, suggestedAgents, formErrors) => {
  if (!value) {
    return undefined;
  }

  const isValidAgent = suggestedAgents.some(agent => agent.label === value || agent.value === value);
  return isValidAgent ? undefined : formErrors.agentNameValidationMsg; 
};

const validateOrginasationCode = (value, organizationDropDownList, formErrors) => {
  if (!value) {
    return undefined; 
  }

  const isValid = organizationDropDownList.some(org => org.value === value.toString());
  return isValid ? undefined : formErrors.agencyNameValidationMsg; 
};